import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Printer, X, CheckCircle2, Sparkles, Package as PackageIcon, Ticket, Send, Loader2, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return iso; }
};

const Row = ({ label, value, testId, strong }) => (
  <div className="flex justify-between gap-4 text-sm" data-testid={testId}>
    <span className="text-slate-500">{label}</span>
    <span className={`text-right ${strong ? 'font-semibold text-slate-900' : 'font-medium text-slate-900'}`}>{value}</span>
  </div>
);

const Divider = () => <div className="border-b border-dashed border-slate-300" />;

const KIND_META = {
  initial: { title: 'Package Purchase', icon: PackageIcon, tone: 'bg-purple-600' },
  balance: { title: 'Package Balance Payment', icon: PackageIcon, tone: 'bg-purple-600' },
  redemption: { title: 'Package Redemption', icon: Ticket, tone: 'bg-indigo-600' },
};

// Items table: service × qty with used/left per line.
const PackageItems = ({ pkg, highlightServiceId }) => (
  <table className="w-full text-sm" data-testid="receipt-package-items">
    <thead>
      <tr className="text-[11px] uppercase tracking-wider text-slate-400">
        <th className="text-left font-medium pb-1">Service</th>
        <th className="text-right font-medium pb-1">Qty</th>
        <th className="text-right font-medium pb-1">Used</th>
        <th className="text-right font-medium pb-1">Left</th>
      </tr>
    </thead>
    <tbody>
      {(pkg.items || []).map((it) => {
        const total = Number(it.quantity_total || 0);
        const used = Number(it.quantity_consumed || 0);
        const hl = highlightServiceId && it.service_id === highlightServiceId;
        return (
          <tr key={it.service_id} className={hl ? 'bg-indigo-50' : ''}>
            <td className={`py-1 ${hl ? 'font-semibold text-indigo-800' : 'text-slate-800'}`}>{it.service_name}</td>
            <td className="py-1 text-right tabular-nums">{total}</td>
            <td className="py-1 text-right tabular-nums">{used}</td>
            <td className={`py-1 text-right tabular-nums ${hl ? 'font-semibold text-indigo-800' : ''}`}>{Math.max(0, total - used)}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

// Printable POS receipt. `payment` is a /payments row (regular, package initial/balance, or ₹0 redemption).
export const PaymentReceipt = ({ payment, open, onClose, canSend = true }) => {
  const [clinic, setClinic] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState(null);

  useEffect(() => { setSentTo(null); }, [payment?.id]);

  const sendWhatsApp = async () => {
    if (!payment?.id || sending) return;
    setSending(true);
    try {
      const res = await api.post(`/payments/${payment.id}/send-receipt`);
      setSentTo(res.data?.to || payment.patient_phone);
      toast.success(`Receipt sent on WhatsApp to ${res.data?.to || payment.patient_phone}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send receipt on WhatsApp');
    } finally { setSending(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/payments/${payment.id}/receipt.pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `Receipt-${payment.receipt_number}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Could not download PDF');
    }
  };

  useEffect(() => {
    if (!open) return;
    api.get('/clinic-info').then((r) => setClinic(r.data)).catch(() => setClinic(null));
  }, [open]);

  useEffect(() => {
    if (!open || !payment?.package_id) { setPkg(null); return; }
    if (payment.package_snapshot) { setPkg(payment.package_snapshot); return; }
    api.get(`/packages/${payment.package_id}`).then((r) => setPkg(r.data?.package || null)).catch(() => setPkg(null));
  }, [open, payment?.package_id, payment?.package_snapshot]);

  if (!payment) return null;

  const kind = payment.package_payment_kind || null;
  const meta = kind ? KIND_META[kind] : null;
  const isRedemption = kind === 'redemption';
  const splits = payment.payment_splits?.length ? payment.payment_splits.filter((s) => s.payment_method_id !== '__coupon__') : null;
  const cancelled = payment.status === 'cancelled';
  const couponAmt = Number(payment.coupon_amount || 0);
  const bill = Number(payment.amount || 0);
  const paid = kind ? bill : bill - couponAmt;
  const HeaderIcon = meta?.icon || CheckCircle2;
  const pkgTotalLeft = pkg ? (pkg.items || []).reduce((s, it) => s + Math.max(0, Number(it.quantity_total || 0) - Number(it.quantity_consumed || 0)), 0) : null;
  const pointsEarned = Number(payment.reward_points_earned || 0);
  const pointsBalance = payment.reward_balance_after ?? payment.reward_available_points;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden receipt-dialog max-h-[92vh] overflow-y-auto" data-testid="payment-receipt-dialog">
        <DialogTitle className="sr-only">Payment receipt {payment.receipt_number}</DialogTitle>
        <DialogDescription className="sr-only">Printable receipt for payment {payment.receipt_number}</DialogDescription>

        <div id="receipt-print" className="receipt-paper bg-white text-slate-900" data-receipt-kind={kind || 'regular'}>
          {/* Header */}
          <div className="px-7 pt-6 pb-5 text-center">
            <p className={`text-xs font-bold uppercase tracking-[0.3em] mb-3 ${isRedemption ? 'text-indigo-700' : kind ? 'text-purple-700' : 'text-emerald-700'}`} data-testid="receipt-heading">Payment Receipt</p>
            <div className={`mx-auto mb-3 w-12 h-12 rounded-2xl ${meta?.tone || 'bg-emerald-600'} text-white flex items-center justify-center`}>
              <HeaderIcon className="w-7 h-7" />
            </div>
            <h2 className="text-xl font-bold tracking-tight" data-testid="receipt-clinic-name">{clinic?.clinic_name || 'Clinic'}</h2>
            {clinic?.clinic_address && <p className="text-xs text-slate-500 mt-1">{clinic.clinic_address}</p>}
            {clinic?.clinic_phone && <p className="text-xs text-slate-500">Ph: {clinic.clinic_phone}</p>}
            {clinic?.clinic_gstin && <p className="text-xs text-slate-500 font-mono" data-testid="receipt-gstin">GSTIN: {clinic.clinic_gstin}</p>}
            {meta && (
              <p className="mt-3 text-[11px] uppercase tracking-[0.25em] text-slate-400" data-testid="receipt-title">{meta.title}</p>
            )}
          </div>
          <Divider />

          {/* Receipt meta */}
          <div className="px-7 py-4 space-y-1.5">
            <Row label="Receipt No." value={<span className="font-mono" data-testid="receipt-number">{payment.receipt_number}</span>} />
            <Row label="Date" value={fmtDateTime(payment.created_at)} />
            {payment.package_ref_number && <Row label="Package" value={<span className="font-mono">{payment.package_ref_number}</span>} testId="receipt-package-ref" />}
            {payment.appointment_ref_number && <Row label="Appointment" value={<span className="font-mono">{payment.appointment_ref_number}</span>} />}
          </div>
          <Divider />

          {/* Patient */}
          <div className="px-7 py-4 space-y-1.5">
            <Row label="Patient" value={payment.patient_name} testId="receipt-patient-name" />
            <Row label="Phone" value={payment.patient_phone} />
          </div>
          <Divider />

          {/* Body — differs by receipt kind */}
          {isRedemption ? (
            <div className="px-7 py-4">
              <div className="rounded-xl bg-indigo-50 border border-indigo-100 px-4 py-3" data-testid="receipt-redeemed-service">
                <p className="text-[11px] uppercase tracking-wider text-indigo-500">Service redeemed</p>
                <p className="text-base font-semibold text-indigo-900 mt-0.5">{payment.service_name || 'Service'}</p>
                <p className="text-xs text-indigo-700 mt-1">1 credit used from {payment.package_ref_number}</p>
              </div>
              {pkg && (
                <div className="mt-4">
                  <PackageItems pkg={pkg} highlightServiceId={payment.service_id} />
                  <div className="mt-3 flex justify-between text-sm">
                    <span className="text-slate-500">Credits remaining</span>
                    <span className="font-semibold" data-testid="receipt-credits-left">{pkgTotalLeft}</span>
                  </div>
                  {pkg.expiry_date && <Row label="Valid till" value={fmtDate(pkg.expiry_date)} />}
                  {Number(pkg.balance || 0) > 0 && (
                    <Row label="Package balance due" value={<span className="text-orange-600">{fmtMoney(pkg.balance)}</span>} />
                  )}
                </div>
              )}
              <div className="flex justify-between items-end mt-4 pt-3 border-t border-slate-200">
                <span className="text-xs uppercase tracking-wider text-slate-500">Amount Paid</span>
                <span className="text-3xl font-bold text-indigo-700" data-testid="receipt-amount">₹0</span>
              </div>
              <p className="text-right text-xs text-slate-500 mt-1">Settled with package credit</p>
            </div>
          ) : kind ? (
            <div className="px-7 py-4">
              {pkg && (
                <>
                  <PackageItems pkg={pkg} />
                  <div className="mt-3 space-y-1.5 pt-3 border-t border-slate-200">
                    <Row label="Package value" value={fmtMoney(pkg.total_cost)} testId="receipt-package-value" />
                    <Row label={kind === 'initial' ? 'Paid now' : 'Paid now (balance)'} value={fmtMoney(payment.amount)} strong />
                    <Row label="Total paid" value={fmtMoney(pkg.total_paid)} />
                    <Row
                      label="Balance due"
                      value={<span className={Number(pkg.balance || 0) > 0 ? 'text-orange-600 font-semibold' : 'text-emerald-700 font-semibold'}>{Number(pkg.balance || 0) > 0 ? fmtMoney(pkg.balance) : 'Fully paid'}</span>}
                      testId="receipt-package-balance"
                    />
                    {pkg.expiry_date && <Row label="Valid till" value={fmtDate(pkg.expiry_date)} />}
                  </div>
                </>
              )}
              <div className="flex justify-between items-end mt-4 pt-3 border-t border-slate-200">
                <span className="text-xs uppercase tracking-wider text-slate-500">Amount Paid</span>
                <span className={`text-3xl font-bold ${cancelled ? 'line-through text-slate-400' : 'text-purple-700'}`} data-testid="receipt-amount">
                  {fmtMoney(payment.amount)}
                </span>
              </div>
            </div>
          ) : (
            <div className="px-7 py-4">
              <div className="flex justify-between text-sm">
                <span className="text-slate-900 font-medium" data-testid="receipt-service">{payment.service_name || 'Service'}</span>
                <span className="font-medium">{fmtMoney(bill)}</span>
              </div>
              {couponAmt > 0 && (
                <div className="flex justify-between text-sm text-emerald-700 mt-1" data-testid="receipt-coupon">
                  <span>Discount coupon {payment.coupon_code ? `(${payment.coupon_code})` : ''}</span>
                  <span>− {fmtMoney(couponAmt)}</span>
                </div>
              )}
              <div className="flex justify-between items-end mt-4 pt-3 border-t border-slate-200">
                <span className="text-xs uppercase tracking-wider text-slate-500">Amount Paid</span>
                <span className={`text-3xl font-bold ${cancelled ? 'line-through text-slate-400' : 'text-emerald-700'}`} data-testid="receipt-amount">
                  {fmtMoney(paid)}
                </span>
              </div>
            </div>
          )}
          {cancelled && (
            <p className="pb-3 text-center text-xs font-bold text-rose-600 tracking-widest uppercase" data-testid="receipt-cancelled">Cancelled</p>
          )}
          <Divider />

          {/* Payment method */}
          <div className="px-7 py-4 space-y-1.5">
            {isRedemption ? (
              <Row label="Paid via" value="Package credit" testId="receipt-method" />
            ) : splits && splits.length > 1 ? (
              splits.map((s, i) => <Row key={i} label={s.payment_method_name} value={fmtMoney(s.amount)} />)
            ) : (
              <Row label="Paid via" value={payment.payment_method_name || splits?.[0]?.payment_method_name || '—'} testId="receipt-method" />
            )}
            {payment.counsellor_name && <Row label="Collected by" value={payment.counsellor_name} />}
            {payment.remarks && <Row label="Note" value={payment.remarks} />}
          </div>

          {/* Rewards */}
          {pointsEarned > 0 && (
            <div className="mx-7 mb-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-3" data-testid="receipt-rewards">
              <Sparkles className="w-4 h-4 text-amber-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-amber-800">+{pointsEarned} reward points earned</p>
                {pointsBalance != null && <p className="text-xs text-amber-700">Balance: {pointsBalance} points</p>}
              </div>
            </div>
          )}

          <p className="px-7 pb-6 pt-2 text-center text-xs text-slate-500">
            Thank you for choosing {clinic?.clinic_name || 'us'}. This is a computer-generated receipt.
          </p>
        </div>

        <div className="receipt-actions p-4 border-t bg-slate-50 space-y-2">
          {canSend && !cancelled && (
            <Button
              className="w-full bg-[#25D366] hover:bg-[#1ebe5d] text-white"
              onClick={sendWhatsApp}
              disabled={sending}
              data-testid="receipt-send-whatsapp-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {sentTo ? `Sent to ${sentTo} · Send again` : `Send PDF on WhatsApp · ${payment.patient_phone || ''}`}
            </Button>
          )}
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose} data-testid="receipt-close-btn">
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
            <Button variant="outline" className="flex-1" onClick={downloadPdf} data-testid="receipt-pdf-btn">
              <FileDown className="w-4 h-4 mr-1" /> PDF
            </Button>
            <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => window.print()} data-testid="receipt-print-btn">
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentReceipt;
